import type { RevealContactDetailsResponse } from "@wallyo/contracts/console/accounts";
import { t } from "../../i18n";

/** The server's bounds (`reason` in server/http/validation/console.ts). */
export const REVEAL_REASON_MIN_LENGTH = 3;
export const REVEAL_REASON_MAX_LENGTH = 500;

/** A problem with the reason, or null when it can be sent. */
export function revealReasonProblem(reason: string): string | null {
  return reason.trim().length >= REVEAL_REASON_MIN_LENGTH ? null : t("customers.reveal.tooShort");
}

/**
 * Showing a customer's contact details. Never optimistic: the details exist
 * only in `revealed`, which only the server's answer reaches. They live in
 * this page view's memory and nowhere else: leaving the page, or "Masquer",
 * drops them.
 */
export type RevealState =
  | { status: "masked" }
  | { status: "asking"; reason: string; problem: string | null; error: unknown }
  | { status: "sending"; reason: string }
  | { status: "revealed"; details: RevealContactDetailsResponse };

export type RevealEvent =
  | { type: "open" }
  | { type: "reason"; reason: string }
  | { type: "submit" }
  | { type: "succeeded"; details: RevealContactDetailsResponse }
  | { type: "failed"; error: unknown }
  | { type: "close" };

export const MASKED: RevealState = { status: "masked" };

export function revealReducer(state: RevealState, event: RevealEvent): RevealState {
  switch (event.type) {
    case "open":
      return state.status === "masked" ? { status: "asking", reason: "", problem: null, error: null } : state;
    case "reason":
      return state.status === "asking" ? { ...state, reason: event.reason, problem: null } : state;
    case "submit": {
      if (state.status !== "asking") return state;
      const problem = revealReasonProblem(state.reason);
      return problem ? { ...state, problem } : { status: "sending", reason: state.reason };
    }
    case "succeeded":
      return state.status === "sending" ? { status: "revealed", details: event.details } : state;
    case "failed":
      return state.status === "sending"
        ? { status: "asking", reason: state.reason, problem: null, error: event.error }
        : state;
    case "close":
      // Also "Masquer": the details are dropped, not hidden.
      return state.status === "sending" ? state : MASKED;
  }
}
